import React, { useEffect } from 'react';
import {
  FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField,
} from '@mui/material';
import { ISubscriptionData } from '../types';
import { IBillingFrequencies, ISalesReps } from '@llws/lift-entity-interfaces';
import { floatVal, intVal } from '@jamesgmarks/utilities';
import {
  getFieldAnimationStyles,
  getRequiredFieldPulseStyles,
} from 'src/components/pages/Subscriptions/form/animationStyles';
import { formatDecimalToPercentage, parsePercentageInput } from '@hydra/internal';
import { useAppSelector } from 'src/redux/hooks';

interface ISubscriptionAccountingSalesProps {
  subscriptionData: ISubscriptionData;
  availableSalesReps: ISalesReps[];
  availableBillingFrequencies: IBillingFrequencies[];
  commissionRateFormValue: string;
  hasCommissionPermissions: boolean;
  setCommissionRateFormValue: React.Dispatch<React.SetStateAction<string>>;
  setSubscriptionData: React.Dispatch<React.SetStateAction<ISubscriptionData>>;
  defaultCommissionForSalesRep: () => Promise<void>;
}

export const SubscriptionAccountingSales: React.FC<ISubscriptionAccountingSalesProps> = ({
  subscriptionData,
  availableSalesReps,
  availableBillingFrequencies,
  commissionRateFormValue,
  hasCommissionPermissions = false,
  setCommissionRateFormValue,
  setSubscriptionData,
  defaultCommissionForSalesRep
}) => {
  const defaultCommissionsForSalesRep = useAppSelector((store) => (
    store.dynamic.data.sales_rep_commissions?.list ?? []));

  useEffect(() => {
    const fetchAndSetCommissionRate = async () => {
      const defaultRate = defaultCommissionsForSalesRep[0]?.commissionRate ?? null;
      if (defaultRate !== null) {
        setCommissionRateFormValue(`${floatVal(`${defaultRate}`)}`);
        setSubscriptionData((old) => ({
          ...old,
          commissionRate: defaultRate,
        }));
      }
    };
    fetchAndSetCommissionRate();
  }, [defaultCommissionsForSalesRep, subscriptionData.salesRepId, subscriptionData.baseSubscriptionId]);
  return (
    <Grid container spacing={3}>
      <Grid item xs={4}>
        <TextField
          label="Revenue tracking code *"
          type="text"
          size="small"
          fullWidth
          className={subscriptionData.revenueTrackingCode.trim().length === 0 ? 'required-empty' : ''}
          sx={{
            background: "white",
            ...getFieldAnimationStyles(),
            ...(subscriptionData.revenueTrackingCode.trim().length === 0 && getRequiredFieldPulseStyles()),
          }}
          value={subscriptionData.revenueTrackingCode}
          onChange={(e) => {
            defaultCommissionForSalesRep()
            setSubscriptionData((old) => ({
              ...old,
              revenueTrackingCode: e.target.value,
            }));
          }}
        />
      </Grid>
      <Grid item xs={4}>
        {availableSalesReps.length > 0 ? (
          <FormControl fullWidth>
            <InputLabel>Sales rep</InputLabel>
            <Select
              label="Sales rep"
              value={subscriptionData?.salesRepId ?? ''}
              size="small"
              sx={{ background: 'white' }}
              onChange={(e) => {
                setSubscriptionData((old) => ({
                  ...old,
                  salesRepId: intVal(e.target.value) ?? null,
                }));
              }}
            >
              {availableSalesReps.map(({ id, user }, i) => (
                <MenuItem key={id ?? i} value={id ?? -1}>
                  {user ? `${user.firstName} ${user.lastName} (${id})` : null}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </Grid>
      { 
        hasCommissionPermissions
        && <Grid item xs={4}>
          <TextField
            label="Commission rate *"
            type="text"
            size="small"
            className={Number.isNaN(floatVal(commissionRateFormValue)) ? 'required-empty' : ''}
            fullWidth
            sx={{
              background: "white",
              ...getFieldAnimationStyles(),
              ...(Number.isNaN(floatVal(commissionRateFormValue)) && getRequiredFieldPulseStyles()),
            }}
            value={floatVal(commissionRateFormValue) < 1 ?formatDecimalToPercentage(commissionRateFormValue) : commissionRateFormValue}
            onChange={(e) => {
              setCommissionRateFormValue(`${parsePercentageInput(e.target.value)}`);
            }}
            onBlur={() => {
              setSubscriptionData((old) => ({
                ...old,
                commissionRate: (
                  commissionRateFormValue === ''
                    ? 0
                    : floatVal(commissionRateFormValue)
                ),
              }));
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Grid>
      }

      <Grid item xs={4}>
        {availableBillingFrequencies.length > 0 ? (
          <FormControl fullWidth>
            <InputLabel>Billing frequency</InputLabel>
            <Select
              label="Billing frequency"
              value={subscriptionData?.billingFrequencyId ?? ''}
              size="small"
              sx={{ background: 'white' }}
              onChange={(e) => {
                setSubscriptionData((old) => ({
                  ...old,
                  billingFrequencyId: intVal(e.target.value) ?? null,
                }));
              }}
            >
              {availableBillingFrequencies.map(({ id, name }, i) => (
                <MenuItem key={id ?? i} value={id ?? -1}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </Grid>
    </Grid>
  );
};
